import { IgniteResponse } from '@clubhub/ignite/models';
import {
  AffordabilityOffReason,
  CriteriaType,
  ICriteriaUpdateItem,
  LenderNameApplicationToOfferDefinition,
  LenderNameCreditType,
  LenderNameTimePeriod,
  LendingTypeCode,
  MiSaveOutcomeModel,
  OutcomeResults,
  SearchType,
} from '@shared/models';
import { QuestionType } from '@shared/models/criteria-issue';
import { MatchedLenderProducts } from './matched-product';

export function igniteIsSuitable(outcome: string | undefined) {
  switch (outcome) {
    case OutcomeResults.Yes:
      return { outcome: 'yes', cssClass: 'text-dark-blue' };
    case OutcomeResults.No:
      return { outcome: 'no', cssClass: 'text-dark-blue' };
    case OutcomeResults.Refer:
    case OutcomeResults.Unset:
      return { outcome: 'refer', cssClass: 'text-warning' };
    default:
      return { outcome: '', cssClass: 'text-dark-blue' };
  }
}
export interface INode {
  id: number | undefined;
  text: string | undefined;
}

export class OutcomeDetailsCard {
  public answerId: number = 0;
  public context: string | undefined;
  public contextId?: number;
  public issue: string = '';
  public issueId?: number;
  public criteriaContextLendingTypeId?: number;
  public criteriaContextVersionId?: number;
  public outcomeType?: CriteriaType;
  public questionsAndAnswers: {
    question: string | undefined;
    answer: string | any | undefined;
    answerName?: string;
    questionId?: number;
    answerId?: number;
    answerType?: QuestionType;
    answers?: INode[];
    answerUnits?: string;
  }[] = [];
  public static card(card: OutcomeDetailsCard, endOfLabelText: string) {
    return {
      answerId: card.answerId,
      context: card.context,
      outcomeType: card.outcomeType,
      contextId: card.contextId,
      issueId: card.issueId,
      issue: `${card.issue}${endOfLabelText}`,
      criteriaContextLendingTypeId: card.criteriaContextLendingTypeId,
      criteriaContextVersionId: card.criteriaContextVersionId,
      questionsAndAnswers: card.questionsAndAnswers.map(cq => {
        return {
          question: cq.question,
          answer: cq.answer,
          answerType: cq.answerType,
          answerId: cq.answerId,
          questionId: cq.questionId,
          answers: cq.answers,
          answerName: cq.answerName,
        };
      }),
    };
  }
}

export class OutcomeLenderContextNote {
  public title: string = '';
  public notes: string[] = [];
  public note?: string;
}

export interface OutcomeResponseOverallResult {
  lenderId: number;
  lender: string;
  lenderUrl: string;
  lenderLogoUrl: string;
  affordabilityCalculatorUrl: string;
  lenderNotesIndex: number;
  lenderNotes: string;
  outcomeResult: string;
  verified: boolean;
  canAcceptApplicantOnline: boolean;
  canAcceptOnlineDipAip: boolean;
  canUploadDocumentsOntoWebsite: boolean;
  hasLiveChat: boolean;
  hasManualUnderwriter: boolean;
  creditType?: LenderNameCreditType;
  applicationToOfferDefinition?: LenderNameApplicationToOfferDefinition;
  applicationToOfferTime?: number;
  applicationToOfferPeriod?: LenderNameTimePeriod;
  initialCaseAssessmentTime?: number;
  initialCaseAssessmentPeriod?: LenderNameTimePeriod;
  isAffordabilityCalculatorOn: boolean;
  affordabilityOffReason: AffordabilityOffReason | null;
  lenderContextNotes: OutcomeLenderContextNote[];
  outcomeNotes: string[];
}

export class OutcomeHistoryDetailsCard {
  public answerId: number = 0;
  public context: INode | undefined;
  public issue: INode | undefined;
  public outcomeType?: CriteriaType;
  public questionsAndAnswers: {
    question: INode;
    answer: INode;
    answers?: INode[];
    answerType?: QuestionType; }[] = [];
  public autoAdded: boolean = false;
  public criteriaContextLendingTypeId?: number;
  public criteriaContextVersionId?: number;
  public static card(card: OutcomeHistoryDetailsCard, endOfLabelText: string) {
    return {
      answerId: card.answerId,
      context: card.context?.text,
      outcomeType: card.outcomeType,
      contextId: card.context?.id,
      issueId: card.issue?.id,
      issue: `${card.issue?.text} ${endOfLabelText}`,
      criteriaContextLendingTypeId: card.criteriaContextLendingTypeId,
      criteriaContextVersionId: card.criteriaContextVersionId,
      questionsAndAnswers: card.questionsAndAnswers.map(cq => {
        return {
          question: cq.question.text,
          answer: cq.answer.text,
          answerType: cq.answerType,
          answerId: cq.answer.id,
          questionId: cq.question.id,
          answers: cq.answers,
          answerName: cq.answer.text,
        };
      }),
    };
  }
}

export interface OutcomeResponseContextResult {
  autoAdded: boolean;
  outcomeResult: string;
  outcomeId: number;
  contextId: number;
  context: string;
  verified: boolean;
  isProperty: boolean;
  contextGroup: boolean;
  contextGroupContextIds: number[];
  criteriaType?: CriteriaType;

  // client side only properties
  showDetails?: boolean;
}

export interface SkipListResponseResult {
  name: string;
  value: string;
}

export interface OutcomeResponse {
  outcomeContextCards?: OutcomeDetailsCard[];
  overallResult: OutcomeResponseOverallResult;
  contextResults: OutcomeResponseContextResult[];
  skipList: SkipListResponseResult[];
}
export interface AffordabilityResult {
  productType: LendingTypeCode;
  productTypeName: string;
  affordabilityResults: IgniteResponse[];
  intuitiveCriteria: OutcomeResponse[];
  additionalCriteria: OutcomeResponse[];
  autoAddedOutcomeIds: number[];
  uiModelJson: string;
  saveOutcomeModel: MiSaveOutcomeModel;
  products: MatchedLenderProducts[];
  outcomeContextCards?: OutcomeHistoryDetailsCard[];
  intuitiveOutcomesContextCards?: OutcomeHistoryDetailsCard[];
  createdDate: Date;
}

export interface OutcomeDetails {
  dateTime: string;
  contexts: OutcomeDetailsCard[];
  details: OutcomeResponse[];
  lendingType: string;
  autoAddedOutcomeIds: number[];
  criteriaSearchId: string | null;
}

export interface PropertyOutcomeDetailsDetailed {
  dateTime: string;
  contexts: OutcomeHistoryDetailsCard[];
  details: OutcomeResponse[];
  intuitiveDetails: OutcomeResponse[];
  propertySearchId: string;
  autoAddedOutcomeIds: number[];
}

export interface OutcomeResult {
  id: number;
  lender: string;
  outcomeResult: string;
  outComeId: number;
  lenderId: number;
}

export interface OutcomeLoginModel {
  verifiableCode: number;
  key: string;
}

export interface OutcomeLoginModelResponse {
  outcomeHistoryId: string;
  verifiableCode: number;
}

export interface OutcomeHistoryViewModel {
  id: string;
  createdDate: Date;
  answerId: number;
  surname: string;
  postcode: string;
  agencyNumber: number;
  criteriaSearchId: string;
  affordabilitySearchId: string;
  productsSearchId: string;
  daysRemaining: string;
  appNames: string[];
  notes: string;
  lendingType: string;
  autoAdded: boolean;
  hasManualAdded: boolean;
  source: SearchType;
}

export interface IOutcomeQuestionsResultContext {
  lenderOutcomes: ICriteriaUpdateItem[];
  lenderConsidered: boolean;
  contextName: string;
}

export interface CriteriaLenders {
  lenderName?: string | null;
  lenderLogoUrl?: string | null;
}
export interface CriteriaResponse {
  pollJobID: number | null;
  criteriaSearchId: string;
  quotes: IgniteResponse[];
  errorMessage: string;
  lenders?: CriteriaLenders[];
}
